import { type Texture, RGBFormat, RGBAFormat } from '@seddi/three';
import log from 'loglevel';
import * as FlagsModule from '../flags/flagsModule';
import { FeatFlag } from '../flags/flagTypes';

/**
 * Get pixels in CPU.
 * @param texture Texture.
 */
const getTextureData = (texture: Texture): ImageData => {
  // This is the only way, right now, to get the data in the CPU for an imported image
  // https://stackoverflow.com/questions/68102995/is-it-possible-to-get-pixels-from-imagebitmap-web-object-without-a-canvas
  // The recommended way is to render it on a canvas
  // https://developer.mozilla.org/en-US/docs/Web/API/ImageBitmap

  const canvas = document.createElement('canvas');
  canvas.height = texture.image.height;
  canvas.width = texture.image.width;

  const context = canvas.getContext('2d');
  context.scale(1, -1);
  context.drawImage(texture.image, 0, -canvas.height, canvas.width, canvas.height);

  const data = context.getImageData(0, 0, canvas.width, canvas.height);
  return data;
};

const toImageDataArray = (elements: Iterable<number>): ImageDataArray => {
  return Uint8ClampedArray.from(elements);
};

/**
 * Applies a 3×3 Gaussian blur via two 1D passes (horizontal then vertical),
 * using wrap-around sampling at the borders so tiles stay continuous.
 *
 * Kernel 1D: [1, 2, 1] normalized by 4.
 */
const gaussianBlur = (
  data: ImageDataArray,
  width: number,
  height: number,
  channels: number,
): ImageDataArray => {
  const len = data.length;
  const temp = new Uint8ClampedArray(len);
  const out = new Uint8ClampedArray(len);
  const stride = width * channels;

  // 1) Horizontal pass with wrap-around
  for (let y = 0; y < height; ++y) {
    const rowOffset = y * stride;
    for (let x = 0; x < width; ++x) {
      const xm1 = (x - 1 + width) % width;
      const xp1 = (x + 1) % width;
      const base = rowOffset + x * channels;

      for (let c = 0; c < channels; ++c) {
        const left = data[rowOffset + xm1 * channels + c];
        const mid = data[base + c];
        const right = data[rowOffset + xp1 * channels + c];
        // (1*left + 2*mid + 1*right) / 4
        temp[base + c] = (left * 1 + mid * 2 + right * 1) / 4;
      }
    }
  }

  // 2) Vertical pass with wrap-around
  for (let y = 0; y < height; ++y) {
    const ym1 = (y - 1 + height) % height;
    const yp1 = (y + 1) % height;
    const rowOff = y * stride;
    const rowOffM = ym1 * stride;
    const rowOffP = yp1 * stride;

    for (let x = 0; x < width; ++x) {
      const base = rowOff + x * channels;
      for (let c = 0; c < channels; ++c) {
        const up = temp[rowOffM + x * channels + c];
        const mid = temp[base + c];
        const down = temp[rowOffP + x * channels + c];
        // (1*up + 2*mid + 1*down) / 4
        out[base + c] = (up * 1 + mid * 2 + down * 1) / 4;
      }
    }
  }

  return out;
};

/**
 * Generate custom mipmaps.
 * @param texture Renderer texture.
 * @param tags Texture tags.
 */
export const generateCustomMipMaps = (texture: Texture, tags?: string[]) => {
  const isDataImg = texture.image.data != undefined;
  let data: ImageDataArray = !isDataImg ? getTextureData(texture).data : toImageDataArray(texture.image.data);
  let height = texture.image.height;
  let width = texture.image.width;

  const mipmaps = [new ImageData(data, width, height)];

  let channels = 0;
  if (texture.format === RGBFormat) {
    channels = 3;
  } else if (texture.format == RGBAFormat) {
    channels = 4;
  } else {
    log.error('Unsupported format generate custom mipmaps');
    return;
  }

  const toIndex = (x: number, y: number, width: number) => y * width + x;

  // enable mipmap filtering only for fabric materials.
  const withFilter = tags?.includes('FABRIC') && FlagsModule.isEnabled(FeatFlag.MIPMAPS_GAUSSIAN_BLUR);

  // main down-sample loop for both dims > 1
  while (width > 1 && height > 1) {
    if (withFilter) {
      // apply 3×3 Gaussian blur convolution
      data = gaussianBlur(data, width, height, channels);
    }

    const mipWidth = Math.max(Math.floor(width / 2), 1);
    const mipHeight = Math.max(Math.floor(height / 2), 1);
    const dataTo = new Uint8ClampedArray(channels * mipWidth * mipHeight);

    for (let y = 0; y < mipHeight; ++y) {
      for (let x = 0; x < mipWidth; ++x) {
        // average 4 samples
        const od = toIndex(x, y, mipWidth) * channels;
        const osx = x * 2;
        const osy = y * 2;
        const o1 = toIndex(osx, osy, width) * channels;
        const o2 = o1 + channels;
        const o3 = toIndex(osx, osy + 1, width) * channels;
        const o4 = o3 + channels;
        dataTo[od] = (data[o1] + data[o2] + data[o3] + data[o4]) >> 2;
        dataTo[od + 1] = (data[o1 + 1] + data[o2 + 1] + data[o3 + 1] + data[o4 + 1]) >> 2;
        dataTo[od + 2] = (data[o1 + 2] + data[o2 + 2] + data[o3 + 2] + data[o4 + 2]) >> 2;
        if (channels === 4) {
          dataTo[od + 3] = (data[o1 + 3] + data[o2 + 3] + data[o3 + 3] + data[o4 + 3]) >> 2;
        }
      }
    }

    mipmaps.push(new ImageData(dataTo, mipWidth, mipHeight));
    data = dataTo;
    width = mipWidth;
    height = mipHeight;
  }

  // handle the “collapsed” case when one dimension reaches 1 first
  while (width > 1 || height > 1) {
    if (withFilter) {
      // apply 3×3 Gaussian blur convolution
      data = gaussianBlur(data, width, height, channels);
    }

    const mipWidth = Math.max(Math.floor(width * 0.5), 1);
    const mipHeight = Math.max(Math.floor(height * 0.5), 1);
    const dataTo = new Uint8ClampedArray(channels * mipWidth * mipHeight);

    for (let y = 0; y < mipHeight; y++) {
      for (let x = 0; x < mipWidth; x++) {
        // average 2 samples
        const od = toIndex(x, y, mipWidth) * channels;
        let o1!: number;
        let o2!: number;

        if (width === 1) {
          // vertical collapse
          const osy = y * 2;
          o1 = toIndex(0, osy, width) * channels;
          o2 = toIndex(0, osy + 1, width) * channels;
        } else {
          // horizontal collapse
          const osx = x * 2;
          o1 = toIndex(osx, 0, width) * channels;
          o2 = toIndex(osx + 1, 0, width) * channels;
        }

        dataTo[od] = (data[o1] + data[o2]) >> 1;
        dataTo[od + 1] = (data[o1 + 1] + data[o2 + 1]) >> 1;
        dataTo[od + 2] = (data[o1 + 2] + data[o2 + 2]) >> 1;
        if (channels === 4) {
          dataTo[od + 3] = (data[o1 + 3] + data[o2 + 3]) >> 1;
        }
      }
    }

    mipmaps.push(new ImageData(dataTo, mipWidth, mipHeight));
    data = dataTo;
    width = mipWidth;
    height = mipHeight;
  }

  texture.mipmaps = mipmaps;
};
